import type { NotebookStep } from './steps';

// 'prompt' cells record the user message that generated the cells after them —
// they render as a chat bubble, never execute, and give the generation model
// the conversation thread when the notebook is sent back as context.
// 'r' cells run in an R notebook's IRkernel (DS-6); 'python' and 'sql' in a
// Python notebook's IPython kernel. A notebook has one kernel language.
export type CellKind = 'markdown' | 'python' | 'sql' | 'r' | 'step' | 'prompt';

/** The kernel a notebook runs (DS-6). */
export type NotebookLanguage = 'python' | 'r';

/** Container cells encapsulate their own child steps (a Pipeline or a Logic
 * Function under construction) — distinct from the flat `CellKind`s above,
 * which never hold nested steps. */
export type ContainerKind = 'pipeline' | 'logic-function';

export type CellOutput = {
  status: 'ok' | 'error';
  outputs: { mime_type: string; data: unknown; stream?: string }[];
  error?: { ename: string; evalue: string; traceback: string[] };
  executed_at: string;
};

export interface Cell {
  id: string;
  kind: CellKind;
  source: string;
  output?: CellOutput;
  running?: boolean;
  // Present only for kind === 'step': the structured Logic-Studio block this
  // cell represents. `source` then mirrors the block as pretty JSON for editing.
  block?: NotebookStep;
}

/** A Pipeline or Logic Function under construction — its steps live embedded
 * here rather than as sibling top-level cells, so step ids never collide
 * across independent containers and export always operates on exactly this
 * container's own steps (see notebookToPipeline/notebookToLogicFunction). */
export interface ContainerCell {
  id: string;
  kind: ContainerKind;
  name: string;
  steps: NotebookStep[];
  // Pipeline-only: set when the user asked for a recurring run ("every day
  // at 8am") — created atomically with the pipeline on Export, instead of a
  // separate trip to the Cron Schedules tab.
  schedule?: { name: string; cron_expression: string; enabled?: boolean };
}

export type AnyCell = Cell | ContainerCell;

export function isContainerCell(cell: AnyCell): cell is ContainerCell {
  return cell.kind === 'pipeline' || cell.kind === 'logic-function';
}

export interface Notebook {
  id: string;
  name: string;
  description: string;
  cells: AnyCell[];
  /** The kernel language (DS-6, default python) and the libraries it loads
   *  (DS-3) — names from that language's curated catalog, installed outside
   *  the kernel by the env builder. */
  environment?: { packages: string[]; language?: NotebookLanguage };
  createdAt: string;
  updatedAt: string;
}
